import React from 'react'
import { Box } from '@mui/material'
import type { Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import VerifiedUser from '@mui/icons-material/VerifiedUser'
import QuestionMark from '@mui/icons-material/QuestionMark'
import type { Person } from '~/shared/types'

type AccountType = Person['account_type']

interface AccountTypeStatusRingProps {
  accountType: AccountType
  size?: number
  badgeSize: number
  children: React.ReactNode
  sx?: SystemStyleObject<Theme>
}

const RING_STYLE: Record<
  AccountType,
  { borderStyle: 'solid' | 'dashed'; icon: React.ReactElement; label: string }
> = {
  verified: { borderStyle: 'solid', icon: <VerifiedUser />, label: 'Verified' },
  unknown: { borderStyle: 'dashed', icon: <QuestionMark />, label: 'Unknown account' },
}

// Renders the account-type signal as a status ring + corner glyph around
// whatever avatar/icon is passed as children, instead of a word-pill —
// see the "Member Banner Badge" design doc, options 1a (banner) / 2a (search).
const AccountTypeStatusRing: React.FC<AccountTypeStatusRingProps> = ({
  accountType,
  size,
  badgeSize,
  children,
  sx,
}) => {
  const style = RING_STYLE[accountType]
  const ringColor = accountType === 'verified' ? 'primary.main' : 'grey.300'
  const badgeColor = accountType === 'verified' ? 'primary.main' : 'grey.400'
  const badgeIconSize = Math.round(badgeSize * 0.6)
  const isFixedSize = size != null

  return (
    <Box
      data-testid="account-type-status-ring"
      title={style.label}
      sx={[
        isFixedSize
          ? { position: 'relative', flex: 'none', width: size, height: size }
          : { position: 'relative', display: 'inline-flex', flex: 'none' },
        sx ?? {},
      ]}
    >
      {isFixedSize ? (
        <Box
          sx={{
            borderColor: ringColor,
            width: size,
            height: size,
            borderRadius: '50%',
            boxSizing: 'border-box',
            borderWidth: 2,
            borderStyle: style.borderStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            '& > svg': { fontSize: Math.round(size * 0.6) },
          }}
        >
          {children}
        </Box>
      ) : (
        <>
          {children}
          <Box
            sx={{
              borderColor: ringColor,
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              boxSizing: 'border-box',
              borderWidth: 2,
              borderStyle: style.borderStyle,
              pointerEvents: 'none',
            }}
          />
        </>
      )}
      <Box
        sx={{
          bgcolor: badgeColor,
          borderColor: 'common.white',
          color: 'common.white',
          position: 'absolute',
          right: -3,
          bottom: -3,
          width: badgeSize,
          height: badgeSize,
          borderRadius: '50%',
          borderWidth: 2,
          borderStyle: 'solid',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& svg': { fontSize: badgeIconSize },
        }}
      >
        {style.icon}
      </Box>
    </Box>
  )
}

export default AccountTypeStatusRing
